import { Component, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'app/app.reducer';
import { SPINNER_ICON, STAR_ICON, STAR_ICON_FILL } from 'app/constants';
import { eq, find } from 'lodash';
import { OutletProfile, Notification, TopicProfile } from 'app/models';
import {
  addTopicFromDialog,
  addTopicToProject,
  deleteTopicFromManager,
  deleteTopicToProject,
  resetSearch,
  resetTopicProfile,
  showNotification,
  tabControl,
  unSetSearch,
  updateTopic,
} from 'app/redux/actions';

@Component({
  selector: 'app-topic-contact-profile',
  templateUrl: './topic-contact-profile.component.html',
  styles: [
    `
      :host .p-button.p-button-icon-only {
        width: 20px !important;
        border-color: transparent;
      }

      :host .p-button.p-button-icon-only .p-button-icon {
        font-size: 0.9rem;
      }

      :host .p-button:focus {
        box-shadow: none !important;
      }

      :host .p-button:hover {
        background: none !important;
      }
    `,
  ],
})
export class TopicProfileComponent implements OnInit {
  public actionType: string;
  public isTopicSaved: boolean;
  public project: any;
  public starToggle: string;
  public topic: TopicProfile;
  public topicsList: any[];
  public isAddedingOrIsRemoving: boolean;
  public savedTopic: any;
  public categories: any[];
  public readonly shortcutLabels = ['Primary', 'Secondary'];

  constructor(private store: Store<AppState>) {
    this.topic = null;
    this.isTopicSaved = false;
    this.actionType = null;
    this.project = null;
    this.starToggle = STAR_ICON;
    this.topicsList = [];
    this.isAddedingOrIsRemoving = false;
    this.savedTopic = null;
    this.categories = [];
  }

  ngOnInit() {
    this.store.select('project').subscribe(({ projectSelected }) => {
      if (projectSelected) {
        this.project = projectSelected;
      }
    });

    this.store.select('category').subscribe(({ categories }) => {
      this.categories = categories || [];
    });

    this.store.select('topicDetails').subscribe(({ topicContactProfile }) => {
      this.topic = null;
      if (topicContactProfile) {
        this.topic = topicContactProfile;
        this.setTopicSaved();
      }
    });

    this.store
      .select('topics')
      .subscribe(
        ({
          topics,
          isAddingTopic,
          isDeletingTopic,
          hasTopicDeleted,
          hasTopicAdded,
          addTopicToProjectError,
          removeTopicToProjectError,
        }) => {
          this.topicsList = topics;
          if (this.topic) {
            this.setTopicSaved();
          }
          if (isAddingTopic || isDeletingTopic) {
            this.starToggle = SPINNER_ICON;
          }
          if (hasTopicAdded) {
            this.setTopicSaved();
          }
          if (hasTopicDeleted) {
            this.setTopicSaved();
          }
          if (addTopicToProjectError) {
            this.store.dispatch(
              showNotification({
                notify: new Notification(
                  `Error adding topic to project`,
                  'error',
                  'tl'
                ),
              })
            );
            this.setTopicSaved();
          }

          if (removeTopicToProjectError) {
            this.store.dispatch(
              showNotification({
                notify: new Notification(
                  `Error removing topic to project`,
                  'error',
                  'tl'
                ),
              })
            );
            this.setTopicSaved();
          }
        }
      );
  }

  handleSaveTopic(topic) {
    if (!this.isAddedingOrIsRemoving) {
      this.isAddedingOrIsRemoving = true;
      this.starToggle = SPINNER_ICON;
      if (this.isTopicSaved) {
        this.store.dispatch(
          deleteTopicToProject({
            topic,
            userProject: this.project,
          })
        );
        this.store.dispatch(deleteTopicFromManager({ topic }));
      } else {
        this.store.dispatch(
          addTopicToProject({
            topic,
            userProject: this.project,
          })
        );
        this.store.dispatch(addTopicFromDialog({ topic }));
      }
    }
  }

  handleTopicLabel(labelName: string) {
    const category = find(
      this.categories,
      (item) => String(item.name).toLowerCase() === labelName.toLowerCase()
    );
    if (!category || !this.savedTopic) {
      this.store.dispatch(
        showNotification({
          notify: new Notification(
            `The ${labelName} label is not available`,
            'error',
            'tl'
          ),
        })
      );
      return;
    }
    this.isAddedingOrIsRemoving = true;
    this.store.dispatch(
      updateTopic({
        topic: {
          ...this.savedTopic,
          categoryId: this.topicLabel === labelName ? null : category.id,
        },
        userTopicId: this.savedTopic.userTopicId,
        userId: null,
        userProject: this.project,
      })
    );
  }

  handleIgnoreTopic(currentlyIgnored: boolean) {
    if (currentlyIgnored) {
      this.handleSaveTopic(this.topic);
      return;
    }
    const ignore = find(this.categories, (item) => String(item.name).toLowerCase() === 'ignore');
    if (!ignore) {
      this.store.dispatch(showNotification({
        notify: new Notification('The Ignore label is not available', 'error', 'tl'),
      }));
      return;
    }
    this.isAddedingOrIsRemoving = true;
    this.store.dispatch(addTopicToProject({
      topic: { ...this.topic, categoryId: ignore.id },
      userProject: this.project,
    }));
  }

  get isTopicIgnored(): boolean {
    return this.topicLabel === 'Ignore';
  }

  get topicLabel(): string {
    return (
      this.savedTopic?.categoryName || this.savedTopic?.label?.name || null
    );
  }

  handleBackToHome() {
    this.store.dispatch(resetTopicProfile());
    this.store.dispatch(tabControl({ index: 0 }));
    this.store.dispatch(unSetSearch());
    this.store.dispatch(resetSearch({ onReset: true }));
  }

  private setTopicSaved() {
    setTimeout(() => {
      if (this.topic) {
        this.savedTopic = find(this.topicsList, (i) => eq(i.id, this.topic.id));
        this.isTopicSaved = !!this.savedTopic;
        this.topic = {
          ...this.topic,
          userTopicId: this.savedTopic?.userTopicId,
        } as any;
        this.starToggle = this.isTopicSaved ? STAR_ICON_FILL : STAR_ICON;
        this.isAddedingOrIsRemoving = false;
      }
    }, 500);
  }
}
